import { z } from "zod";
import { blockSchema } from "./blocks";
import { blockVisibilitySchema } from "@tellescope/visibility-schema";
import { countBlocksOfType } from "./blockScanning";

// Snippet schema - reusable key-value pairs for template interpolation
export const snippetSchema = z.object({
  id: z.string().uuid(),
  key: z.string().regex(/^[a-zA-Z][a-zA-Z0-9_]*$/, "Key must start with letter, alphanumeric/underscore only"),
  value: z.string(),
});

// Child page schema - a sub-page nested one level under a top-level page.
// Has no `pages` field of its own, so nesting depth is capped at one by construction.
export const childPageSchema = z.object({
  id: z.string().uuid(),
  slug: z.string().regex(/^[a-z0-9-]+$/, "Slug must be lowercase alphanumeric with hyphens"),
  title: z.string().min(1, "Title is required"),
  blocks: z.array(blockSchema),
  visibility: blockVisibilitySchema.optional(),
});

// Page schema - a single top-level page in the portal, optionally with one level of sub-pages
export const pageSchema = z.object({
  id: z.string().uuid(),
  slug: z.string().regex(/^[a-z0-9-]+$/, "Slug must be lowercase alphanumeric with hyphens"),
  title: z.string().min(1, "Title is required"),
  blocks: z.array(blockSchema),
  visibility: blockVisibilitySchema.optional(),
  pages: z.array(childPageSchema).optional().default([]),
});

// OpenLoop Chat validation patterns - single source of truth for Zod schema and runtime validation
export const OPENLOOP_CHAT_VALIDATION_PATTERNS = {
  source: /^https:\/\/[a-zA-Z0-9-]+\.my\.connect\.aws\/[a-zA-Z0-9/_-]+\.js$/,
  snippetId: /^[A-Za-z0-9+/=]{100,}$/,
  sessionId: /^[a-zA-Z0-9_-]{1,100}$/,
  platformName: /^[a-zA-Z0-9\s\-'.&]{1,100}$/,
} as const;

// OpenLoop Chat integration schema - Amazon Connect chat widget configuration
export const openLoopChatIntegrationSchema = z.object({
  /** When true, the OpenLoop chat widget is enabled */
  enabled: z.boolean().optional().default(false),
  /** Amazon Connect script URL */
  source: z.string().regex(OPENLOOP_CHAT_VALIDATION_PATTERNS.source, "Invalid Amazon Connect script URL").optional(),
  /** Base64 encoded snippet ID (typically 500+ chars) */
  snippetId: z.string().regex(OPENLOOP_CHAT_VALIDATION_PATTERNS.snippetId, "Invalid snippet ID format").optional(),
  /** Session identifier (used as the script element ID) */
  sessionId: z.string().regex(OPENLOOP_CHAT_VALIDATION_PATTERNS.sessionId, "Invalid session ID format").optional(),
  /** Platform name for chat routing (must be pre-configured in Amazon Connect) */
  platformName: z
    .string()
    .regex(OPENLOOP_CHAT_VALIDATION_PATTERNS.platformName, "Invalid platform name format")
    .optional(),
  /** Header message displayed in the chat widget (appears under logo) */
  headerMessage: z.string().max(50, "Header message must be 50 characters or less").optional(),
  /** URL for the logo displayed in the chat widget header */
  logoUrl: z.string().url("Must be a valid URL").optional(),
  /** Alt text for the logo image */
  logoAltText: z.string().max(200, "Logo alt text must be 200 characters or less").optional(),
  /** Display name shown on live agent messages in the chat transcript */
  agentDisplayName: z.string().max(50, "Agent display name must be 50 characters or less").optional(),
  /** Display name shown on supervisor messages in the chat transcript */
  supervisorDisplayName: z.string().max(50, "Supervisor display name must be 50 characters or less").optional(),
  /** Display name shown on automated (contact flow and bot) messages; Amazon Connect caps it at 26 characters */
  automatedMessageDisplayName: z
    .string()
    .max(26, "Automated message display name must be 26 characters or less")
    .optional(),
  /** When true, no icons are shown next to transcript messages */
  hideMessageIcons: z.boolean().optional(),
  /** Public https URL for the icon shown next to live agent messages */
  agentIconUrl: z.string().url("Must be a valid URL").startsWith("https://", "Must be an https URL").optional(),
  /** Public https URL for the icon shown next to automated messages */
  automatedMessageIconUrl: z
    .string()
    .url("Must be a valid URL")
    .startsWith("https://", "Must be an https URL")
    .optional(),
  /** Optional conditional-visibility rule; widget shows to everyone when omitted (default). */
  visibility: blockVisibilitySchema.optional(),
});

// Season integration schema - dietitian-portal partner-redirect configuration.
// Customers connect Season server-side; the portal uses the JWT minted by the
// /integrations/proxy_read endpoint and stamps it into the configured URL template.
//
// The post-login auto-redirect flow only fires when redirectTemplate is set AND
// a dietitian_portal_redirect_to value was captured from the URL. Direct-click
// resolution (via {{season.token}} in rich-text links) is independent of this
// schema entirely — those links work as long as the backend mints a token.
//
// `dietitian_portal_redirect_to` is appended automatically by the click handlers
// when a captured value is pending; customers should NOT include it in their URLs.
export const seasonIntegrationSchema = z.object({
  /** Partner banner value (e.g. "CUSTOMER_NAME") supplied by Season. Referenced
   * from any URL via the {{season.banner}} placeholder. */
  banner: z.string().optional(),
  /** URL template for the partner redirect-back endpoint.
   * Supports the {{season.token}} (per-click JWT) and {{season.banner}} placeholders. */
  redirectTemplate: z.string().url().optional(),
});

// ZenDesk Web Widget validation pattern
export const ZENDESK_SNIPPET_KEY_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// ZenDesk Web Widget integration schema
export const zendeskIntegrationSchema = z
  .object({
    /** When true, the ZenDesk Web Widget is enabled */
    enabled: z.boolean().optional().default(false),
    /** ZenDesk snippet key UUID (the key= parameter from the snippet script URL) */
    snippetKey: z.string().optional(),
    /** Widget API namespace — Classic (pre June 2023 accounts) or Messaging (current) */
    widgetVersion: z.enum(["classic", "messaging"]).optional(),
    // V2 reserved — inert in V1, included to avoid schema breaking changes when auth is added
    /** When true, JWT-based user identity is passed to the widget (V2) */
    authEnabled: z.boolean().optional().default(false),
    /** URL of the server-side JWT endpoint for widget authentication (V2) */
    jwtEndpoint: z
      .string()
      .url()
      .refine((url) => url.startsWith("https://"), "Must be an HTTPS URL")
      .optional(),
  })
  .superRefine((val, ctx) => {
    if (val.enabled && val.snippetKey !== undefined && !ZENDESK_SNIPPET_KEY_PATTERN.test(val.snippetKey)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Must be a valid UUID",
        path: ["snippetKey"],
      });
    }
  });

// Integrations schema - container for all integration configurations
export const integrationsSchema = z.object({
  openLoopChat: openLoopChatIntegrationSchema.optional().default({}),
  season: seasonIntegrationSchema.optional().default({}),
  zendesk: zendeskIntegrationSchema.optional().default({}),
});

// Font family options - curated list of Google Fonts
export const FONT_FAMILY_OPTIONS = [
  { value: "system", label: "System Default" },
  { value: "Inter", label: "Inter" },
  { value: "Roboto", label: "Roboto" },
  { value: "Open Sans", label: "Open Sans" },
  { value: "Lato", label: "Lato" },
  { value: "Poppins", label: "Poppins" },
  { value: "Montserrat", label: "Montserrat" },
  { value: "Nunito", label: "Nunito" },
  { value: "Source Sans Pro", label: "Source Sans Pro" },
  { value: "Raleway", label: "Raleway" },
  { value: "Playfair Display", label: "Playfair Display" },
  { value: "Merriweather", label: "Merriweather" },
] as const;

const hexColorSchema = z.string().regex(/^#[0-9A-Fa-f]{6}$/, "Must be a valid hex color (e.g., #1e40af)");

// Theming schema - organization color customization
export const themingSchema = z.object({
  /** Primary brand color in hex format (e.g., "#1e40af") */
  primaryColor: hexColorSchema.optional(),
  /** Secondary brand color in hex format */
  secondaryColor: hexColorSchema.optional(),
  /** Body text color in hex format */
  textColor: hexColorSchema.optional(),
  /** Navigation/header background color in hex format */
  headerColor: hexColorSchema.optional(),
  /** Button text (foreground) color — useful when primary color is dark */
  buttonTextColor: hexColorSchema.optional(),
  /** Border radius preset — controls roundness of UI elements */
  borderRadius: z.enum(["square", "default", "rounded"]).optional(),
  /** Font family name (from curated list or custom) — applies to all text */
  fontFamily: z.string().optional(),
  /** Custom Google Fonts CSS URL for advanced users */
  customFontUrl: z.string().url().optional(),
  /** Font family for heading elements (H1–H4) — overrides fontFamily for headings */
  headingFont: z.string().optional(),
  /** Font family for body/paragraph text — overrides fontFamily for body text */
  bodyFont: z.string().optional(),
});

// Layout schema - portal layout configuration
export const layoutSchema = z.object({
  /** When true, shows a sidebar navigation on larger screens (lg breakpoint: 1024px+) */
  enableSidebarNavigation: z.boolean().optional().default(false),
});

// New forms experience rollout mode - off, every form, or every form on/off a list of exceptions
export const newFormsExperienceModeSchema = z.enum(["off", "all", "selected", "all_except"]);

// Portal settings schema - configuration options for portal behavior
export const portalSettingsSchema = z.object({
  /** Custom description shown on the sign-in page below the "Sign In" title */
  loginDescription: z.string().max(200, "Login description must be 200 characters or less").optional(),
  /** Custom label for the email/phone input on the sign-in page */
  loginInputLabel: z.string().max(100, "Login input label must be 100 characters or less").optional(),
  /** When true, unauthenticated users are redirected to login instead of seeing the landing page */
  disableLandingPage: z.boolean().optional().default(false),
  /** When true, registration is disabled and /register redirects to login */
  disableRegistration: z.boolean().optional().default(false),
  /** When true, SMS is disabled as an MFA option and only email is allowed */
  disableSMSForMFA: z.boolean().optional().default(false),
  /**
   * Which forms use the new in-portal forms experience instead of the legacy embedded frame.
   * `"selected"` and `"all_except"` read `newFormsExperienceFormIds` as an allowlist and a
   * denylist respectively.
   */
  newFormsExperience: newFormsExperienceModeSchema.optional().default("off"),
  /**
   * Form ids the mode applies to: the only forms on the new experience under `"selected"`, and the
   * only forms held back from it under `"all_except"`. Ignored by the other modes.
   */
  newFormsExperienceFormIds: z.array(z.string()).optional().default([]),
  /** Integration configurations */
  integrations: integrationsSchema.optional().default({}),
  /** Theming configuration for portal colors */
  theming: themingSchema.optional().default({}),
  /** Layout configuration for portal navigation */
  layout: layoutSchema.optional().default({}),
});

// Portal schema - the complete portal configuration
export const portalSchema = z
  .object({
    version: z.literal(1),
    name: z.string().min(1, "Portal name is required"),
    snippets: z.array(snippetSchema).optional().default([]),
    pages: z.array(pageSchema),
    settings: portalSettingsSchema.optional().default({}),
    updatedAt: z.string().datetime(),
  })
  .superRefine((val, ctx) => {
    if (countBlocksOfType(val, "shop") > 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "A portal can only have one Shop block",
        path: ["pages"],
      });
    }
  });

// Inferred types
export type Snippet = z.infer<typeof snippetSchema>;
export type ChildPage = z.infer<typeof childPageSchema>;
export type Page = z.infer<typeof pageSchema>;
export type OpenLoopChatIntegration = z.infer<typeof openLoopChatIntegrationSchema>;
export type SeasonIntegration = z.infer<typeof seasonIntegrationSchema>;
export type ZendeskIntegration = z.infer<typeof zendeskIntegrationSchema>;
export type Integrations = z.infer<typeof integrationsSchema>;
export type Theming = z.infer<typeof themingSchema>;
export type Layout = z.infer<typeof layoutSchema>;
export type NewFormsExperienceMode = z.infer<typeof newFormsExperienceModeSchema>;
export type PortalSettings = z.infer<typeof portalSettingsSchema>;
export type PortalSchema = z.infer<typeof portalSchema>;
